import type { ApiResource } from '../schema';

export const resource: ApiResource = {
  id: 'tickets',
  slug: 'tickets',
  name: 'Tickets',
  description: 'The PSA help desk: create, update, and track tickets with SLA clocks, priority, and device linkage.',
  icon: 'ConfirmationNumber',
  endpoints: [
    {
      slug: 'list',
      method: 'GET',
      path: '/api/tickets',
      summary: 'List tickets',
      description: 'Returns every ticket in the current tenant, newest first. Supports the same status/priority/assignee filters as the Helpdesk list view.',
      auth: 'session',
      params: [{ name: 'status', in: 'query', type: 'string', description: 'Filter to one status: Open, In Progress, Waiting, Resolved, Closed.' }],
      responseExample: `[
  {
    "id": "tkt_8f2a",
    "title": "Wi-Fi down in Room 4",
    "status": "Open",
    "priority": "High",
    "type": "Incident",
    "requester": "Jordan Klein",
    "requesterEmail": "jklein@example.org",
    "assignedTo": "tech_maria",
    "deviceIds": ["dev_10ac"],
    "createdAt": "2026-07-18T14:02:11.000Z"
  }
]`,
      sourceFile: 'routes/tickets-psa.js, lib/schemas.js',
    },
    {
      slug: 'create',
      method: 'POST',
      path: '/api/tickets',
      summary: 'Create a ticket',
      description: 'Creates a ticket. Priority derives automatically from impact x urgency when both are given, otherwise defaults to Medium.',
      auth: 'session',
      requestBodyExample: `{
  "title": "Printer offline in the library",
  "description": "Won't respond to any print jobs since this morning.",
  "requester": "Alex Rivera",
  "requesterEmail": "arivera@example.org",
  "impact": "Medium",
  "urgency": "High"
}`,
      responseExample: `{
  "id": "tkt_9c14",
  "title": "Printer offline in the library",
  "status": "Open",
  "priority": "High",
  "createdAt": "2026-07-20T09:14:02.000Z"
}`,
      sourceFile: 'routes/tickets-psa.js, lib/schemas.js',
    },
    {
      slug: 'add-time-entry',
      method: 'POST',
      path: '/api/tickets/:id/time',
      summary: 'Log a time entry',
      description: 'Adds a billable or non-billable work-log entry to a ticket. Contributes to the ticket\'s billable total and the tenant\'s timesheet reporting.',
      auth: 'session',
      params: [{ name: 'id', in: 'path', type: 'string', required: true, description: 'Ticket id.' }],
      requestBodyExample: `{ "minutes": 45, "note": "Replaced the access point.", "billable": true }`,
      responseExample: `{ "ok": true, "totalMinutes": 45 }`,
      sourceFile: 'routes/tickets-psa.js',
    },
    {
      slug: 'sla-response',
      method: 'GET',
      path: '/api/sla-response',
      summary: 'Get response-SLA settings',
      description: 'Returns the per-priority first-response SLA minutes configured for this tenant.',
      auth: 'session',
      responseExample: `{ "Urgent": 30, "High": 60, "Medium": 240, "Low": 480 }`,
      sourceFile: 'routes/tickets-psa.js',
    },
    {
      slug: 'update-fields',
      method: 'PATCH',
      path: '/api/tickets/:id',
      summary: 'Update ticket fields',
      description: 'Updates a narrow, allowlisted set of ticket fields in one call: status, priority, assignedTo, category, dueDate. Any other field in the request body is rejected with a 400 — this is not a general-purpose ticket editor, it backs the Helpdesk grid\'s inline cell editing. A status change into Resolved or Closed sets the matching lifecycle timestamp automatically.',
      auth: 'session',
      params: [{ name: 'id', in: 'path', type: 'string', required: true, description: 'Ticket id.' }],
      requestBodyExample: `{ "status": "Resolved", "assignedTo": "tech_maria" }`,
      responseExample: `{
  "id": "tkt_8f2a",
  "status": "Resolved",
  "priority": "High",
  "assignedTo": "tech_maria",
  "resolvedAt": "2026-07-20T09:20:00.000Z",
  "updatedAt": "2026-07-20T09:20:00.000Z"
}`,
      sourceFile: 'routes/tickets-inline.js',
    },
    {
      slug: 'add-note',
      method: 'POST',
      path: '/api/tickets/:id/notes',
      summary: 'Add an internal note',
      description: 'Appends an internal note to a ticket. Internal notes are staff/admin-only — they never appear in the requester-facing view of a ticket.',
      auth: 'session',
      params: [{ name: 'id', in: 'path', type: 'string', required: true, description: 'Ticket id.' }],
      requestBodyExample: `{ "text": "Escalated to the network vendor for an RMA." }`,
      responseExample: `{
  "id": "tkt_8f2a",
  "internalNotes": [
    { "id": "note_44a1", "at": "2026-07-20T09:21:00.000Z", "by": "tech_maria", "text": "Escalated to the network vendor for an RMA." }
  ],
  "updatedAt": "2026-07-20T09:21:00.000Z"
}`,
      sourceFile: 'routes/tickets-psa.js',
    },
    {
      slug: 'bulk-update',
      method: 'POST',
      path: '/api/tickets/bulk/update',
      summary: 'Bulk update tickets',
      description: 'Applies the same patch — any of status, priority, assignedTo, board, category, stage — to up to 1000 tickets by id in one call. Only the fields present in the patch object are changed; a status change still drives the same resolvedAt/closedAt lifecycle timestamps as a single-ticket update.',
      auth: 'session',
      requestBodyExample: `{
  "ids": ["tkt_8f2a", "tkt_9c14"],
  "patch": { "status": "Closed", "assignedTo": "tech_maria" }
}`,
      responseExample: `{ "updated": 2, "patch": { "status": "Closed", "assignedTo": "tech_maria" } }`,
      sourceFile: 'routes/tickets-bulk.js',
    },
    {
      slug: 'business-hours',
      method: 'GET',
      path: '/api/settings/business-hours',
      summary: 'Get business-hours settings',
      description: 'Returns the tenant\'s configured business-hours and holiday calendar, or the defaults (Mon-Fri 08:00-16:00, weekends closed) if none has been saved. Times are interpreted at a fixed UTC offset (the tz field, minutes east of UTC) rather than an IANA timezone. Used by the business-hours-aware SLA-clock math elsewhere in the API.',
      auth: 'session',
      responseExample: `{
  "tz": 0,
  "days": {
    "sun": { "open": "08:00", "close": "16:00", "closed": true },
    "mon": { "open": "08:00", "close": "16:00", "closed": false },
    "tue": { "open": "08:00", "close": "16:00", "closed": false },
    "wed": { "open": "08:00", "close": "16:00", "closed": false },
    "thu": { "open": "08:00", "close": "16:00", "closed": false },
    "fri": { "open": "08:00", "close": "16:00", "closed": false },
    "sat": { "open": "08:00", "close": "16:00", "closed": true }
  },
  "holidays": ["2026-11-26", "2026-12-25"]
}`,
      sourceFile: 'routes/businesshours.js, lib/businesshours.js',
    },
  ],
};
